export type ProjectStatus = "live" | "private" | "coming-soon";

export interface CodeExcerpt {
  title: string;
  caption: string;
  language: string;
  code: string;
  file?: string;
}

export interface Project {
  name: string;
  slug: string;
  description: string;
  longDescription: string;
  tech: string[];
  status: ProjectStatus;
  gradient: string;
  accent: string;
  features: string[];
  screenshots: string[];
  image?: string;
  url?: string;
  // Optional case-study fields. When present, render in the deep dive at /work/[slug].
  problem?: string;
  architecture?: string[];
  codeExcerpts?: CodeExcerpt[];
  aiWorkflow?: string;
  timeline?: string;
  // Optional: a live, guided product walkthrough to spotlight on the case
  // study (e.g. BidFlow's estimate→proposal→payment flow). When present,
  // renders a featured highlight block with a direct CTA.
  guidedFlow?: { url: string; label: string; blurb: string };
}

export const PROJECTS: Project[] = [
  // === Top tier: flagship projects ===
  {
    name: "Alpha Lab",
    slug: "alpha-lab",
    description:
      "Educational stock trading platform with real-time market data, portfolio tracking, and gamified learning. Users trade with virtual currency while learning real market dynamics.",
    longDescription:
      "Alpha Lab is a full-featured educational trading platform designed to teach stock market fundamentals through hands-on experience. Users start with virtual currency and build portfolios using real-time market data. The platform includes gamified progression, achievement badges, and leaderboards to keep learners engaged. Built as both a web app and native iOS app using Capacitor.",
    tech: ["React", "Firebase", "Capacitor", "Node.js", "Tailwind CSS"],
    status: "live",
    gradient: "from-emerald-500/30 via-emerald-500/10 to-transparent",
    accent: "emerald",
    features: [
      "Real-time stock market data and portfolio tracking",
      "Virtual currency trading with no financial risk",
      "Gamified learning with achievements and leaderboards",
      "Native iOS app via Capacitor",
      "Firebase-powered real-time data sync",
    ],
    screenshots: [],
    image: "/images/projects/alpha-lab-icon.png",
    url: "https://alphalabslearning.vercel.app",
  },
  {
    name: "BidFlow",
    slug: "bidflow",
    description:
      "AI-powered contractor bidding platform with smart proposal generation, Stripe Connect payments, and real-time project management. Streamlines the entire bid-to-invoice workflow.",
    longDescription:
      "BidFlow is an AI-powered platform that transforms the contractor bidding process. Using Anthropic's Claude AI, it generates professional proposals from simple project descriptions. Contractors can manage the full lifecycle from bid to invoice with Stripe Connect handling payments between clients and contractors. Real-time project management keeps everyone in sync, and the AI learns from past proposals to improve over time.",
    tech: ["Next.js", "Supabase", "Stripe Connect", "Anthropic AI", "TypeScript"],
    status: "live",
    gradient: "from-teal-500/30 via-teal-500/10 to-transparent",
    accent: "teal",
    features: [
      "AI-generated professional proposals via Claude",
      "Stripe Connect for contractor/client payments",
      "Full bid-to-invoice workflow management",
      "Real-time project tracking and updates",
      "Supabase-powered backend with row-level security",
      "Smart proposal improvement over time",
    ],
    screenshots: [
      "/images/projects/bidflow-screenshots/img_7434.webp",
      "/images/projects/bidflow-screenshots/img_7435.webp",
      "/images/projects/bidflow-screenshots/img_7448.webp",
      "/images/projects/bidflow-screenshots/img_7460.webp",
      "/images/projects/bidflow-screenshots/img_7461.webp",
    ],
    image: "/images/projects/bidflow.png",
    url: "https://bidflow-ai.com",
    timeline: "Solo · ~3 months · 630 files · ~105K lines TypeScript",
    guidedFlow: {
      url: "https://bidflow-ai.com/guide",
      label: "Walk the live product flow",
      blurb:
        "See the whole loop end to end — describe a job, watch the AI generate a priced line-item estimate, send a branded proposal, and collect payment. It's the real app, guided step by step.",
    },
    problem:
      "Contractors lose hours on every estimate — measuring rooms, pricing scope, writing proposals, chasing payments. Most send rough numbers via text, lose bids to faster-responding competitors, and never see the data that would help them win the next one. BidFlow turns photos and a short description into a priced, branded proposal in minutes, then runs the marketplace that gets the contractor paid.",
    architecture: [
      "Next.js 16 App Router with a Capacitor iOS build target — same TypeScript codebase ships as web and native app from one repo.",
      "Supabase Postgres with row-level security; multi-tenant by contractor org with end-to-end auth via @supabase/ssr cookies.",
      "Six distinct Anthropic Claude integration points: estimate generation, blueprint extraction, receipt scanning, photo classification, septic plan extraction, and pre-flight validation.",
      "Stripe Connect with destination charges and platform fee — contractors onboard via Stripe Express; clients pay through hosted Checkout.",
      "@react-pdf/renderer for branded proposal PDFs; Resend for transactional email; Upstash Redis for rate-limiting AI endpoints.",
      "Sentry for production error tracking, Vercel Analytics, Firebase Admin for push notifications, IndexNow auto-submit for SEO.",
    ],
    codeExcerpts: [
      {
        title: "Forced tool use for structured AI output",
        file: "src/app/api/extract-plans/route.ts",
        caption:
          "The plan-extraction endpoint takes 1–4 photos of a blueprint and returns structured scope. The key move is `tool_choice: { type: \"tool\", name: \"extract_plan_details\" }` — Claude must return a tool call matching the schema exactly. Result is Zod-validated before reaching the client. No free-text JSON parsing, no hallucinated keys.",
        language: "ts",
        code: `const EXTRACT_TOOL_SCHEMA = {
  name: "extract_plan_details",
  description: "Extract construction scope from blueprint/plan photos",
  input_schema: {
    type: "object" as const,
    properties: {
      rooms: { type: "array", items: { /* ... */ } },
      scope_items: { type: "array", items: { /* ... */ } },
      questions: { type: "array", description: "Questions for the contractor", items: { /* ... */ } },
    },
  },
};

const response = await anthropic.messages.create({
  model: AI_MODEL,
  temperature: 0,
  system: EXTRACT_SYSTEM_PROMPT,
  tools: [EXTRACT_TOOL_SCHEMA],
  tool_choice: { type: "tool", name: "extract_plan_details" },
  messages: [{ role: "user", content }],
});

const toolUse = response.content.find((b) => b.type === "tool_use");
return jsonResponse(extractPlansResponseSchema, toolUse.input);`,
      },
      {
        title: "Stripe Connect destination charge with platform fee",
        file: "src/app/api/invoices/[id]/pay/route.ts",
        caption:
          "BidFlow is a marketplace — the contractor gets paid, the platform takes a cut. This route validates the contractor's Stripe onboarding state, calculates a percentage fee, then creates a Checkout session with transfer_data.destination (routes funds to the connected account) and application_fee_amount (the platform cut).",
        language: "ts",
        code: `const contractor = proposal.contractors;
if (!contractor.stripe_account_id || !contractor.stripe_onboarding_complete) {
  return NextResponse.json({ error: "Contractor has not set up payments" }, { status: 400 });
}

const amountCents = toCents(Number(invoice.amount));
const feeCents = calculateFeeCents(amountCents, PLATFORM_FEE_PERCENT);

const paymentIntentData: Record<string, unknown> = {
  transfer_data: { destination: contractor.stripe_account_id },
};
if (feeCents > 0) {
  paymentIntentData.application_fee_amount = feeCents;
}

const session = await stripe.checkout.sessions.create({
  mode: "payment",
  line_items: [/* ... */],
  payment_intent_data: paymentIntentData,
});`,
      },
    ],
    aiWorkflow:
      "I built BidFlow with Claude Code as my IDE — every AI feature, every Supabase migration, every Stripe flow was drafted, debugged, and shipped through Claude conversations. The trick was learning to give Claude the same direction I'd want as a developer: schema first, contract second, prompt third. For the AI estimate engine I used Anthropic's tool-use API to force structured outputs — instead of parsing free-text JSON, the model fills a Zod-validated schema. That moved estimate parsing from 'sometimes works' to production-reliable. The six Claude integration points took roughly five days each to ship end-to-end, including UI, error handling, rate limiting, and Sentry instrumentation.",
  },
  {
    name: "PantryBook",
    slug: "pantrybook",
    description:
      "Smart recipe and pantry management app with Claude Vision AI for scanning ingredients and recipes, plus PDF export for beautifully formatted recipe cards.",
    longDescription:
      "PantryBook is a smart recipe and pantry management application that uses Claude's Vision AI to scan and digitize recipes from photos. Users can manage their pantry inventory, discover recipes based on what they have on hand, and export beautifully formatted recipe cards as PDFs using @react-pdf/renderer. Backed by Supabase for real-time data storage and authentication, PantryBook makes it easy to organize your kitchen and never waste ingredients.",
    tech: ["Next.js", "Supabase", "Claude Vision AI", "React PDF", "TypeScript"],
    status: "live",
    gradient: "from-lime-500/30 via-lime-500/10 to-transparent",
    accent: "lime",
    features: [
      "Claude Vision AI for scanning recipes and ingredients",
      "Pantry inventory management",
      "Recipe discovery based on available ingredients",
      "PDF export for beautifully formatted recipe cards",
      "Supabase-powered backend with real-time sync",
      "User authentication and personal recipe collections",
    ],
    screenshots: [],
    image: "/images/projects/pantrybook-icon.png",
    url: "https://pantrybook.vercel.app",
  },
  {
    name: "Social Markets",
    slug: "social-markets",
    description:
      "Social-media content tool for drafting and organizing posts from one dashboard. Built with React 19, Vite, and Supabase. Currently in beta.",
    longDescription:
      "Social Markets is a social-media content tool for drafting, organizing, and managing posts from a single dashboard. The front end is built with React 19 and Vite, with Supabase handling accounts, authentication, and data. The project is in beta and active polish — AI-assisted content generation is on the roadmap but not yet wired in.",
    tech: ["React", "Vite", "Supabase", "TypeScript"],
    status: "live",
    gradient: "from-pink-500/30 via-pink-500/10 to-transparent",
    accent: "pink",
    features: [
      "Single dashboard for managing social-media content",
      "Draft and organize posts in one place",
      "Supabase-powered accounts, auth, and data",
      "OAuth onboarding",
      "React 19 + Vite 8 front end",
      "In beta — actively being polished",
    ],
    screenshots: [],
    image: "/images/projects/social-markets.jpg",
    url: "https://social-markets-live.vercel.app",
  },
  {
    name: "Wyldra",
    slug: "wyldra",
    description:
      "AI nature-documentary director. Describe what you want to film and Claude plans an 8–12 scene documentary, fills in each shot, then a full-screen Shoot Mode camera guides you frame by frame — exporting a finished MP4 in the browser or a Final Cut Pro bundle.",
    longDescription:
      "Wyldra turns your wildlife footage into a narrated nature documentary, in the spirit of the great naturalist filmmakers. You describe what you want to film — a backyard bird feeder, the river bend at dawn — and Claude decides whether it needs more context, optionally runs grounded web research, then plans a full documentary outline of 8–12 scenes before filling in each scene's shots. Shoot Mode is a full-screen camera with a thumb-zone shot ribbon, framing aids (rule-of-thirds grid, horizon level, stability meter), and haptics. When you're done, it renders a finished MP4 in-browser via FFmpeg.wasm or exports a Final Cut Pro bundle. Ships as both a web app and a native iOS app via Capacitor. Currently in beta — in testing, not yet generally available.",
    tech: ["Next.js", "Anthropic AI", "FFmpeg.wasm", "Capacitor", "Zustand"],
    status: "live",
    gradient: "from-green-500/30 via-green-500/10 to-transparent",
    accent: "green",
    features: [
      "Claude plans a full documentary outline, then fills each shot",
      "One-tap grounded web research streams real facts into your notes",
      "Full-screen Shoot Mode with framing aids and haptics",
      "In-browser MP4 render via FFmpeg.wasm",
      "Final Cut Pro bundle export (FCPXML + clips + music)",
      "Native iOS app via Capacitor",
    ],
    screenshots: [],
    image: "/images/projects/wyldra.png",
    url: "https://wyldra.vercel.app",
  },
  {
    name: "MAADU",
    slug: "maadu",
    description:
      "Address-level ADU feasibility tool for the North Shore of Massachusetts. Type a Beverly or Peabody address and it pulls the real parcel, building footprint, zoning, wetlands, and flood data, then draws your lot and the largest backyard apartment that actually fits — to scale.",
    longDescription:
      "MAADU answers one question for a homeowner in seconds: could your backyard be an income property? You pick a town, type your address, and the tool fetches the live parcel boundary, building footprint, zoning district, wetlands, and FEMA flood data straight from municipal and statewide GIS servers, then draws your lot, your house, and the largest ADU (accessory dwelling unit) that fits within the real setbacks — to scale and draggable. Beverly runs on the City of Beverly's ArcGIS layers; Peabody runs on MassGIS statewide layers with setbacks hard-coded from the Peabody zoning ordinance. Because MassGIS is statewide, the same engine extends to any MA town by adding its verified setback table. Built as a single static page — no framework, no build step — with one Vercel serverless function for lead and search capture. Live at maadu.homes, framed as \"Beverly first, expanding across the North Shore.\"",
    tech: ["Vanilla JS", "ArcGIS / MassGIS", "GeoJSON", "Vercel Functions", "Resend"],
    status: "live",
    gradient: "from-emerald-500/30 via-emerald-500/10 to-transparent",
    accent: "emerald",
    features: [
      "Live parcel, footprint, zoning, wetlands & FEMA flood lookup by address",
      "Draws lot, house, and the largest ADU that fits — to scale, draggable",
      "Two GIS backends (Beverly ArcGIS + Peabody MassGIS) behind one engine",
      "Real setback rules per zoning district, not estimates",
      "Estimated build cost and rental-income potential",
      "Single static page, no build step — instant load",
    ],
    screenshots: [],
    image: "/images/projects/maadu.png",
    url: "https://maadu.homes",
    problem:
      "Massachusetts legalized ADUs by right in 2025, but a homeowner has no easy way to know if their specific lot can actually fit one. The answer depends on parcel size, the existing building footprint, the zoning district's setbacks, wetlands, and flood zones — data that lives across separate municipal and state GIS systems. MAADU pulls all of it for a single address and draws the answer to scale in seconds, instead of a paid survey or a call to the building department.",
    architecture: [
      "Single static index.html — inline CSS + JS, no framework and no build step, so the page loads instantly and deploys as plain files.",
      "Beverly backend hits the City of Beverly's ArcGIS Online feature services (parcels, roofprints, zoning, wetlands, septic, FEMA flood) directly from the browser, all CORS-enabled.",
      "Peabody backend uses MassGIS statewide layers (parcels with zoning + lot size, building structures, MassDEP wetlands, FEMA NFHL) with setbacks hard-coded from the Peabody zoning ordinance §7.2.",
      "A robust address resolver normalizes input (suffix-agnostic, unit-stripping) so any local address format resolves to the right parcel.",
      "Lots are drawn square to the road using MassDOT road centerlines, then the largest compliant ADU rectangle is solved and rendered draggable/resizable to scale.",
      "One Vercel serverless function captures lead and search events (Slack webhooks + Resend email); deployed on Vercel, custom domain maadu.homes.",
    ],
    timeline: "Solo · static page + one serverless function · live GIS pipeline",
  },
  {
    name: "JobFlow",
    slug: "jobflow",
    description:
      "Mobile-first PWA for a solo excavation contractor. One tap from the job site fans a project photo out to five channels — Google Business Profile, Facebook, Instagram, the portfolio site, and a scheduled iMessage review request.",
    longDescription:
      "JobFlow is a mobile-first progressive web app built for a solo New Hampshire excavation contractor. From the job site, one tap is designed to publish a project photo and caption across five channels at once: Google Business Profile, a Facebook business page, an Instagram business profile, the contractor's portfolio site, and a scheduled iMessage review request to the customer. It includes a caption engine, an iOS-friendly combobox, retry on failed posts, offline draft persistence, and web push reminders for review requests — all designed to be used one-handed in the field. Built and ready; awaiting Google API approval before the client launch.",
    tech: ["Next.js", "Supabase", "Web Push", "PWA", "TypeScript"],
    status: "coming-soon",
    gradient: "from-blue-500/30 via-blue-500/10 to-transparent",
    accent: "blue",
    features: [
      "One tap publishes to five channels at once",
      "Google Business Profile, Facebook, Instagram, portfolio, iMessage",
      "Offline draft persistence for spotty job-site signal",
      "Web push reminders for scheduled review requests",
      "Retry on failed posts with status tracking",
      "Installable mobile-first PWA",
    ],
    screenshots: [],
    image: "/images/projects/jobflow.png",
    url: "https://gbpflow.vercel.app",
  },
  // === Mid tier ===
  {
    name: "DispatchIQ",
    slug: "dispatchiq",
    description:
      "AI voice receptionist for trades businesses (in development). Designed to answer calls 24/7, quote on the spot from a structured price book, book appointments, and sync to the CRM — so plumbers, HVAC, and electricians stop losing revenue to voicemail.",
    longDescription:
      "DispatchIQ is an in-progress voice-agent concept for trades businesses. The design: each client gets a custom 24/7 AI receptionist built on Vapi.ai and Claude Sonnet 4.6 to handle after-hours and overflow calls, trained on the shop's services, pricing, and service area, producing ranged quotes via a structured tool call (never hallucinating a price). Planned CRM integrations with Jobber, ServiceTitan, and Housecall Pro through an n8n webhook layer, with a text summary to the owner after every booked call. Currently building toward a first pilot.",
    tech: ["Next.js", "Vapi AI", "Anthropic Claude", "ElevenLabs", "TypeScript"],
    status: "coming-soon",
    gradient: "from-violet-500/30 via-violet-500/10 to-transparent",
    accent: "violet",
    features: [
      "24/7 AI voice receptionist, trained per business (planned)",
      "Structured quoting via Claude tool calls — no hallucinated prices",
      "Emergency triage with keyword cue detection",
      "CRM sync to Jobber, ServiceTitan, and Housecall Pro via n8n (planned)",
      "Text-message summaries to the owner after every call",
      "Built on Vapi.ai + Claude Sonnet 4.6",
    ],
    screenshots: [],
  },
  {
    name: "AutoBot",
    slug: "autobot",
    description:
      "Autonomous network monitoring bot with scheduled health checks, real-time alerts, and a clean dashboard for tracking system uptime and performance metrics.",
    longDescription:
      "AutoBot is an autonomous network monitoring platform that continuously watches system health across your infrastructure. A Python FastAPI backend handles scheduled health checks and alert processing, while a React dashboard provides real-time visibility into uptime, latency, and performance metrics. Built with SQLite for lightweight data persistence and APScheduler for reliable task scheduling, AutoBot is designed to run unattended and notify you only when something needs attention.",
    tech: ["Python", "FastAPI", "React", "Vite", "SQLite"],
    status: "coming-soon",
    gradient: "from-rose-500/30 via-rose-500/10 to-transparent",
    accent: "rose",
    features: [
      "Autonomous scheduled network health checks",
      "Real-time uptime and latency monitoring",
      "Alert system for performance anomalies",
      "Clean dashboard for system metrics",
      "FastAPI backend with APScheduler",
      "Lightweight SQLite data persistence",
    ],
    screenshots: [],
    image: "/images/projects/autobot.png",
  },
  {
    name: "PTFlow",
    slug: "ptflow",
    description:
      "Audio-guided physical-therapy workouts. Build a routine by dragging exercises into order, then follow a hands-free audio player that talks you through each rep, set, and rest — so you can keep your eyes off the screen mid-exercise.",
    longDescription:
      "PTFlow turns a physical-therapy routine into a hands-free, audio-guided workout. You build a session by dragging exercises into the order you want, then start a player that narrates each rep, set, and rest interval out loud — so the screen can sit on the floor while you actually do the movement. Built with Next.js and the Anthropic SDK, with drag-and-drop powered by DnD Kit. Currently paused.",
    tech: ["Next.js", "Anthropic AI", "DnD Kit", "TypeScript"],
    status: "coming-soon",
    gradient: "from-purple-500/30 via-purple-500/10 to-transparent",
    accent: "purple",
    features: [
      "Drag-and-drop routine builder",
      "Hands-free audio guidance through every rep and rest",
      "Eyes-off-screen design for mid-exercise use",
      "Per-exercise sets, reps, and timing",
      "Built with the Anthropic SDK",
      "DnD Kit drag-and-drop ordering",
    ],
    screenshots: [],
    image: "/images/projects/ptflow.png",
  },
  {
    name: "PassportIQ",
    slug: "passportiq",
    description:
      "EU Battery Passport compliance tool for US battery exporters. Tracks the data points the regulation requires and generates QR-coded digital passports — turning a compliance headache into a guided workflow.",
    longDescription:
      "PassportIQ helps US battery exporters meet the EU's Battery Passport regulation. It tracks the required compliance data points for each product and generates QR-coded digital passports that resolve to the mandated public information. Built with Next.js, Supabase, and shadcn/ui, it's designed to replace spreadsheets and consultants with a single guided workflow. Currently scaffolded — early concept, not yet in active development.",
    tech: ["Next.js", "Supabase", "shadcn/ui", "TypeScript"],
    status: "coming-soon",
    gradient: "from-yellow-500/30 via-yellow-500/10 to-transparent",
    accent: "yellow",
    features: [
      "Tracks the EU Battery Passport required data points",
      "QR-coded digital passports for each product",
      "Guided compliance workflow",
      "Supabase-backed records with auth",
      "shadcn/ui component system",
      "Built for US-to-EU battery exporters",
    ],
    screenshots: [],
  },
  {
    name: "Maddox UI",
    slug: "maddox-ui",
    description:
      "An experimental 3D interface prototype — a narrative concept where you drag and correlate evidence on a 'neural synthesis board' to argue your case against a probabilistic AI judge. Early WebGL exploration, not a finished game.",
    longDescription:
      "Maddox UI is a personal R&D prototype exploring narrative-driven 3D interfaces. The concept casts you against a probabilistic sentencing AI, correlating evidence on a Neural Synthesis Board inside a reactive WebGL courtroom. It's an early experiment in React Three Fiber and Framer Motion — a sketch of an interaction model rather than a shipped product.",
    tech: ["React", "Three.js", "React Three Fiber", "Framer Motion", "Vite"],
    status: "coming-soon",
    gradient: "from-red-500/30 via-red-500/10 to-transparent",
    accent: "red",
    features: [
      "Experimental WebGL courtroom concept",
      "Neural Synthesis Board interaction sketch",
      "Narrative framing around a probabilistic AI judge",
      "An R&D exploration, not a finished game",
      "Built with React Three Fiber and Framer Motion",
    ],
    screenshots: [],
    image: "/images/projects/maddox-ui.svg",
  },
  {
    name: "Jarvis Quant",
    slug: "jarvis-quant",
    description:
      "Quantitative research platform for analyzing market data, building trading models, and uncovering actionable insights from complex financial datasets.",
    longDescription:
      "Jarvis Quant is a quantitative research platform built for analyzing financial markets. It combines a Python backend for heavy data processing with a React frontend for interactive visualization. Users can build and backtest trading models, analyze market patterns, and generate actionable insights from complex datasets. The platform processes large volumes of market data efficiently and presents findings through intuitive dashboards.",
    tech: ["Python", "React", "Vite", "Tailwind CSS"],
    status: "private",
    gradient: "from-indigo-500/30 via-indigo-500/10 to-transparent",
    accent: "indigo",
    features: [
      "Quantitative market data analysis",
      "Trading model building and backtesting",
      "Interactive data visualization dashboards",
      "Python-powered data processing pipeline",
      "Pattern recognition across financial datasets",
      "Real-time market data integration",
    ],
    screenshots: [],
    image: "/images/projects/jarvis-quant.svg",
  },
  {
    name: "Poly Intel",
    slug: "poly-intel",
    description:
      "Intelligent trading analysis platform with real-time market data processing, pattern recognition, and data-driven insights for smarter investment decisions.",
    longDescription:
      "Poly Intel is an intelligent trading analysis platform that processes real-time market data to surface actionable insights. It uses pattern recognition algorithms to identify trends and opportunities across multiple asset classes. The React frontend provided clean, responsive dashboards while a Python backend handled data ingestion and analysis. A retired project — it ran on live trading and has since been wound down.",
    tech: ["Python", "React", "Vite", "Tailwind CSS"],
    status: "private",
    gradient: "from-orange-500/30 via-orange-500/10 to-transparent",
    accent: "orange",
    features: [
      "Real-time market data processing",
      "Pattern recognition and trend identification",
      "Multi-asset class analysis",
      "Clean, responsive analysis dashboards",
      "Python backend for data processing",
      "Data-driven investment insights",
    ],
    screenshots: [],
    image: "/images/projects/poly-intel.svg",
  },
  {
    name: "CodeBook",
    slug: "codebook",
    image: "/images/projects/codebook.png",
    description:
      "AI-powered code snippet manager and learning tool built with Anthropic's Claude. Generates, explains, and organizes code with streaming AI responses for an instant, interactive experience.",
    longDescription:
      "CodeBook is an AI-powered code snippet manager that helps developers learn, organize, and generate code. Powered by Anthropic's Claude via the Vercel AI SDK, it delivers streaming AI responses for a fast, interactive experience. Users can generate code snippets from natural language prompts, get explanations of complex code, and organize their personal code library. The Vercel AI SDK integration enables real-time streaming output so responses appear instantly as they're generated.",
    tech: ["Next.js", "TypeScript", "Anthropic AI", "Vercel AI SDK", "Tailwind CSS"],
    status: "live",
    gradient: "from-sky-500/30 via-sky-500/10 to-transparent",
    accent: "sky",
    features: [
      "AI-powered code generation from natural language",
      "Streaming responses via Vercel AI SDK",
      "Code explanation and learning tools",
      "Personal code snippet library",
      "Syntax-highlighted code blocks",
      "Built on Anthropic's Claude for accurate code output",
    ],
    screenshots: [],
    url: "https://codebookpro.vercel.app",
  },
  {
    name: "Shalom City Suds",
    slug: "shalom-city-suds",
    description:
      "Modern e-commerce platform with a shopping cart, Stripe checkout, and a beautiful product showcase. Built with the latest Next.js and React.",
    longDescription:
      "Shalom City Suds is a modern e-commerce platform for a premium soap and body care brand. It features a full shopping cart and a visually rich product showcase. Built with Next.js for fast page loads and SEO, it uses Zustand for lightweight state management and integrates with Stripe for secure payments.",
    tech: ["Next.js", "React", "TypeScript", "Zustand", "Tailwind CSS"],
    status: "live",
    gradient: "from-cyan-500/30 via-cyan-500/10 to-transparent",
    accent: "cyan",
    features: [
      "Full e-commerce shopping cart",
      "Stripe payment integration",
      "Beautiful product showcase with rich imagery",
      "SEO-optimized with Next.js static generation",
      "Lightweight state management with Zustand",
    ],
    screenshots: [],
    image: "/images/projects/shalom-city-suds.png",
  },
  {
    name: "Gray Mobile Massage",
    slug: "gray-mobile-massage",
    description:
      "Premium mobile massage service platform with online booking, service showcases, and a warm, inviting design that converts visitors into clients.",
    longDescription:
      "Gray Mobile Massage is a professional service platform built for a mobile massage business. The site features a warm, inviting design that showcases available services, pricing, and booking options. It emphasizes trust-building elements like testimonials and clear service descriptions. The responsive design ensures a seamless experience from phone to desktop, critical for a business whose clients often book on mobile.",
    tech: ["React", "Vite", "Tailwind CSS", "Responsive Design"],
    status: "live",
    gradient: "from-amber-500/30 via-amber-500/10 to-transparent",
    accent: "amber",
    features: [
      "Clean, conversion-focused service showcase",
      "Mobile-first responsive design",
      "Online booking integration",
      "Service and pricing breakdowns",
      "Trust-building layout with testimonials",
      "Fast Vite-powered build pipeline",
    ],
    screenshots: [],
    image: "/images/projects/gray-mobile-massage.jpg",
  },
  {
    name: "SNG Woodwork",
    slug: "sng-woodwork",
    description:
      "Hand-turned bowls and vessels from New Hampshire wood. Stripe Checkout, tamper-evident Certificate of Provenance with HMAC-signed QR codes, Field Notes email registry on Neon Postgres.",
    longDescription:
      "SNG Woodwork is the workshop site of Shiloh N. Gray, a New Hampshire woodturner. The site sells one-of-a-kind hand-turned bowls and vessels through Stripe Checkout with inventory state tracking (available/reserved/sold). Every purchase includes a print-ready Certificate of Provenance — a US Letter-aspect document with HMAC-signed tamper-evident QR codes that resolve to a public verify page. The Field Notes email registry is backed by Neon Postgres and triggers Resend broadcasts via Vercel Cron when new pieces ship to the bench. Commission and tree-offer forms route through Resend with a Postgres backup channel.",
    tech: ["Next.js 16", "Neon Postgres", "Stripe", "Resend", "React PDF"],
    status: "live",
    gradient: "from-amber-500/30 via-amber-500/10 to-transparent",
    accent: "amber",
    features: [
      "Stripe Checkout with multi-state inventory (available/reserved/sold)",
      "Certificate of Provenance — HMAC-signed QR codes verify authenticity",
      "Field Notes email registry on Neon Postgres",
      "Vercel Cron broadcasts new pieces via Resend",
      "Commission & tree-offer forms with Postgres backup channel",
      "Print-ready cert (8.5×11) with embossed seal and microtext security border",
    ],
    screenshots: [],
    image: "/images/projects/sng-woodwork.jpg",
    url: "https://sngwoodwork.vercel.app",
  },
  // === Not started ===
  {
    name: "O'Connor Land Works",
    slug: "oconnor-land-works",
    description:
      "High-end property maintenance and excavation platform. Features service showcases for tree work, site prep, and seasonal maintenance with a focus on quality craftsmanship.",
    longDescription:
      "O'Connor Land Works is a professional property maintenance and excavation platform built for a premier New Hampshire landscaping business. The site showcases a wide range of services including expert tree removal, precision excavation, drainage solutions, and seasonal maintenance. Designed with a 'quality first' philosophy, it features a clean, professional aesthetic that builds trust with residential and commercial clients alike.",
    tech: ["Static HTML", "Upstash", "Resend", "SEO Optimization"],
    status: "live",
    gradient: "from-orange-500/30 via-orange-500/10 to-transparent",
    accent: "orange",
    features: [
      "Service showcases for excavation and tree work",
      "Dynamic quote request integration",
      "Project galleries and service breakdowns",
      "Lakes Region local SEO optimization",
      "Mobile-first responsive architecture",
      "Brand identity integration",
    ],
    screenshots: [],
    image: "/images/projects/oconnor-logo.png",
    url: "https://oconnorlandworks.com",
  },
];

export const accentColors: Record<string, { badge: string; ring: string; text: string }> = {
  emerald: {
    badge: "bg-emerald-500/10 text-emerald-400 ring-emerald-500/20",
    ring: "group-hover:ring-emerald-500/30",
    text: "text-emerald-400",
  },
  amber: {
    badge: "bg-amber-500/10 text-amber-400 ring-amber-500/20",
    ring: "group-hover:ring-amber-500/30",
    text: "text-amber-400",
  },
  cyan: {
    badge: "bg-cyan-500/10 text-cyan-400 ring-cyan-500/20",
    ring: "group-hover:ring-cyan-500/30",
    text: "text-cyan-400",
  },
  violet: {
    badge: "bg-violet-500/10 text-violet-400 ring-violet-500/20",
    ring: "group-hover:ring-violet-500/30",
    text: "text-violet-400",
  },
  rose: {
    badge: "bg-rose-500/10 text-rose-400 ring-rose-500/20",
    ring: "group-hover:ring-rose-500/30",
    text: "text-rose-400",
  },
  indigo: {
    badge: "bg-indigo-500/10 text-indigo-400 ring-indigo-500/20",
    ring: "group-hover:ring-indigo-500/30",
    text: "text-indigo-400",
  },
  orange: {
    badge: "bg-orange-500/10 text-orange-400 ring-orange-500/20",
    ring: "group-hover:ring-orange-500/30",
    text: "text-orange-400",
  },
  sky: {
    badge: "bg-sky-500/10 text-sky-400 ring-sky-500/20",
    ring: "group-hover:ring-sky-500/30",
    text: "text-sky-400",
  },
  lime: {
    badge: "bg-lime-500/10 text-lime-400 ring-lime-500/20",
    ring: "group-hover:ring-lime-500/30",
    text: "text-lime-400",
  },
  pink: {
    badge: "bg-pink-500/10 text-pink-400 ring-pink-500/20",
    ring: "group-hover:ring-pink-500/30",
    text: "text-pink-400",
  },
  teal: {
    badge: "bg-teal-500/10 text-teal-400 ring-teal-500/20",
    ring: "group-hover:ring-teal-500/30",
    text: "text-teal-400",
  },
  blue: {
    badge: "bg-blue-500/10 text-blue-400 ring-blue-500/20",
    ring: "group-hover:ring-blue-500/30",
    text: "text-blue-400",
  },
  green: {
    badge: "bg-green-500/10 text-green-400 ring-green-500/20",
    ring: "group-hover:ring-green-500/30",
    text: "text-green-400",
  },
  purple: {
    badge: "bg-purple-500/10 text-purple-400 ring-purple-500/20",
    ring: "group-hover:ring-purple-500/30",
    text: "text-purple-400",
  },
  yellow: {
    badge: "bg-yellow-500/10 text-yellow-400 ring-yellow-500/20",
    ring: "group-hover:ring-yellow-500/30",
    text: "text-yellow-400",
  },
  red: {
    badge: "bg-red-500/10 text-red-400 ring-red-500/20",
    ring: "group-hover:ring-red-500/30",
    text: "text-red-400",
  },
};
